import { flames, heart, sabre, shards, slash, speed, sprite, swirl, target } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ดาบชาดคลั่งไฟ (cinnabar, yang): a heavy dao at forge heat. Re-themed from blood to heat (10-13): red-hot steel,
 * sparks, molten metal and soot; no blood, bones or skulls anywhere. Colours match tongs-claw.ts / cliff-push-sabre.ts.
 */

const EMBER = '#ff7a2a';
const EMBER_HI = '#ffd27a';
const CINNABAR = '#e0452a';
const IRON = '#a06a52';
const HOT = '#ffe6c0';
const SOOT = '#3a2620';
const HOT_STEEL = '#ffc8a0';

/** The heavy dao heated through: dark grip, glowing steel, a white-hot edge. */
const hotSabre = (p: Pix, x0: number, y0: number, x1: number, y1: number, w = 4) =>
  sabre(p, x0, y0, x1, y1, { hilt: SOOT, blade: HOT_STEEL, w, edge: EMBER_HI, curve: -2.4 });

/** Sparks: little 1-2 px cinders, a hot head and an ember tail. */
function sparks(p: Pix, list: readonly (readonly [number, number])[]): void {
  for (const [x, y] of list) p.px(x, y, EMBER_HI).px(x + 1, y + 1, EMBER);
}

/** A glowing iron bar (the break's billet) from (x0,y0) to (x1,y1), its broken end white-hot. */
function billet(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  p.thick(x0, y0, x1, y1, CINNABAR, 3);
  p.line(x0, y0 - 1, x1, y1 - 1, EMBER);
  p.ell(x1, y1, 2, 2, EMBER);
  p.px(x1, y1, HOT);
}

registerIcons('skill', {
  // The red-hot blade cuts and the sparks fly.
  'bs-slash': (p) => {
    slash(p, 14, 18, 12, -2.6, 0.2, EMBER, 3);
    hotSabre(p, 5, 27, 26, 6);
    sparks(p, [
      [24, 18],
      [27, 23],
      [20, 25],
      [26, 13],
    ]);
  },
  // Two ragged glowing gashes torn through.
  'bs-rend': (p) => {
    for (const x0 of [6, 14]) {
      const pts: [number, number][] = [];
      for (let k = 0; k <= 8; k++) pts.push([x0 + k * 1.6 + (k % 2) * 2, 4 + k * 3]);
      p.path(pts, CINNABAR, 3);
      p.path(pts, EMBER_HI, 1);
    }
    sparks(p, [
      [26, 11],
      [27, 16],
      [25, 22],
    ]);
  },
  // A whirl of embers with the blade in it.
  'bs-whirl': (p) => {
    swirl(p, 16, 16, 13, CINNABAR, { arms: 3, turn: 4.4, core: EMBER });
    hotSabre(p, 9, 23, 25, 8, 3);
  },
  // A charge that leaves scorched steps, onto a marked foe.
  'bs-charge': (p) => {
    for (let k = 0; k < 5; k++) p.rect(3 + k * 3, 22 - k * 2, 2, 2, k % 2 ? EMBER : SOOT);
    speed(
      p,
      12,
      [
        [11, 7],
        [16, 9],
      ],
      EMBER,
    );
    hotSabre(p, 8, 24, 27, 9);
    target(p, 25, 6, 3, C.white);
  },
  // A hot billet struck in two on the anvil, scale and sparks flying.
  'bs-break': (p) => {
    sprite(
      p,
      5,
      21,
      ['hhhhhhhhhhhhhhhhhhhhh', '.iiiiiiiiiiiiiiiiiiii', '.....iiiiiiiiiii.....', '.......iiiiiii.......', '.....iiiiiiiiiii.....', '....sssssssssssss....'],
      { h: IRON, i: '#6e4436', s: SOOT },
    );
    billet(p, 5, 17, 13, 16);
    billet(p, 27, 17, 19, 16);
    p.path(
      [
        [16, 9],
        [15, 12],
        [17, 14],
        [16, 18],
      ],
      EMBER_HI,
      1,
    );
    for (const [x, y] of [
      [10, 8],
      [22, 8],
      [16, 4],
    ] as const)
      p.line(16, 13, x, y, EMBER);
    shards(p, 16, 12, 10, IRON, 4, 0.4);
    sparks(p, [
      [7, 9],
      [25, 5],
      [27, 11],
    ]);
  },
  // Frenzy: a white-hot eye in a blaze.
  'bs-frenzy': (p) => {
    flames(p, 4, 29, 25, 11, CINNABAR, EMBER);
    p.ell(16, 15, 11, 7, EMBER_HI);
    p.ell(16, 15, 4.5, 4.5, CINNABAR);
    p.ell(16, 15, 1.5, 1.5, C.ink);
    p.px(14, 13, C.white);
    p.thick(4, 6, 13, 9, C.ink, 2).thick(28, 6, 19, 9, C.ink, 2);
    for (const x of [8, 24]) p.line(x, 18, x + (x < 16 ? -2 : 2), 23, EMBER);
  },
  // Drawing the heat in: the blade drips molten metal into a crucible, the heart glows.
  'bs-drink': (p) => {
    hotSabre(p, 4, 18, 22, 4, 3);
    p.rect(16, 13, 1, 2, EMBER_HI).rect(17, 18, 1, 2, EMBER);
    sprite(
      p,
      8,
      22,
      ['rrrrrrrrrrrrrrrr', '.bbbbbbbbbbbbbb.', '..bbbbbbbbbbbb..', '...bbbbbbbbbb...', '.....bbbbbb.....'],
      { r: EMBER_HI, b: IRON },
    );
    heart(p, 26, 22, 3, EMBER);
  },
  // A massacre: three hot blades whirling in a storm of embers round a white-hot forge heart.
  'bs-massacre': (p) => {
    swirl(p, 16, 16, 12, CINNABAR, { arms: 3, turn: 3.8 });
    for (let k = 0; k < 3; k++) {
      const a = (k * Math.PI * 2) / 3 - 0.4;
      const x = 16 + Math.cos(a) * 9;
      const y = 16 + Math.sin(a) * 9;
      sabre(p, x - Math.sin(a) * 4, y + Math.cos(a) * 4, x + Math.sin(a) * 5, y - Math.cos(a) * 5, {
        blade: HOT_STEEL,
        hilt: SOOT,
        w: 2.5,
        curve: -1,
      });
    }
    sprite(p, 12, 12, ['..eee..', '.ehhhe.', 'ehhwhhe', '.ehhhe.', '..eee..'], { e: EMBER, h: EMBER_HI, w: HOT });
  },
});
